import {
  BodyText,
  Button,
  Callout,
  Separator,
  Tooltip,
  toast,
} from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { useManualRoutePrescription } from "@/features/Charting/network/useManualRoutePrescription";
import { usePharmacyRoutePrescription } from "@/features/Charting/network/usePharmacyRoutePrescription";
import { trackEvent } from "@/lib/tracking";
import { MedicationRequest } from "@curai/js-fhir-tools";
import { useParams } from "@tanstack/react-router";
import { useAvailableChartingActions } from "../../useAvailableChartingActions";

export const FailedPrescriptionCallout = ({
  medicationRequest,
}: {
  medicationRequest: MedicationRequest;
}) => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const { isMarkPhonedInAvailable, isRetryFailedSubmissionAvailable } =
    useAvailableChartingActions({ patientId: patientId as string });

  const pharmacyRoutePrescription = usePharmacyRoutePrescription({
    patientId,
    encounterId: medicationRequest.encounterId,
    medicationRequestId: medicationRequest.id as string,
  });

  const manualRoutePrescription = useManualRoutePrescription({
    patientId,
    encounterId: medicationRequest.encounterId,
    medicationRequestId: medicationRequest.id as string,
  });

  const onSubmitRoutePharmacy = async () => {
    trackEvent(TRACKERS.SEND_MEDICATION_TO_PHARMACY);
    try {
      const res = await pharmacyRoutePrescription.mutateAsync({
        medicationRequestIds: [medicationRequest.id as string],
      });
      if (res.data.failures.length > 0) {
        const failure = res.data.failures[0];
        toast.error(
          `Failed to resend prescription to pharmacy: ${failure.failureReason}`,
        );
      }
    } catch {
      toast.error("Failed to resend prescription to pharmacy.");
    }
  };

  const onPhoneIn = async () => {
    trackEvent(TRACKERS.MARK_PHONED_IN);
    try {
      await manualRoutePrescription.mutateAsync({
        routingMethod: MedicationRequest.ROUTING_METHOD.values.PHONE,
      });
    } catch {
      toast.error("Failed to manually route prescription.");
    }
  };

  return (
    <>
      <Callout title="Pharmacy Error" variant="danger">
        <div className="flex flex-col gap-2">
          <BodyText size="sm">
            This prescription failed to route to the pharmacy. Click the Resend
            to Pharmacy button to try again, call the pharmacy and mark the
            prescription as phoned in, or void the prescription if a duplicate
            medication has been reordered cleanly
          </BodyText>
          <div className="flex flex-row gap-2">
            <Tooltip
              tooltipContentText={
                !isRetryFailedSubmissionAvailable
                  ? "No privileges to resend to pharmacy."
                  : undefined
              }
            >
              <Button
                size="sm"
                variant="primary"
                onClick={onSubmitRoutePharmacy}
                isLoading={pharmacyRoutePrescription.isPending}
                disabled={
                  !isRetryFailedSubmissionAvailable ||
                  manualRoutePrescription.isPending
                }
              >
                Resend to Pharmacy
              </Button>
            </Tooltip>
            <Tooltip
              tooltipContentText={
                !isMarkPhonedInAvailable
                  ? "No privileges to mark as phoned in."
                  : undefined
              }
            >
              <Button
                size="sm"
                variant="secondary"
                onClick={onPhoneIn}
                isLoading={manualRoutePrescription.isPending}
                disabled={
                  !isMarkPhonedInAvailable ||
                  pharmacyRoutePrescription.isPending
                }
              >
                Mark Phoned In
              </Button>
            </Tooltip>
          </div>
        </div>
      </Callout>
      <Separator />
    </>
  );
};
